import {
  OrganizationEntity,
  OrganizationEntityType,
  PARTNER_ACCOUNT_STATUSES,
  PartnerAccountStatus,
  PartnerOrganization,
  ReportedAccountStatus,
  SubPartnerAccountStatus,
  SupportedCurrencies,
} from 'vopay';
import { OrgChartTreeNode, getInitialExpansion, revealNode } from 'shared/orgChart';
import derivePartnerAccountStatus, {
  DerivedPartnerAccountStatus,
} from '@/components/Entities/Accounts/partnerAccountStatus';

// The Organization Overview's tree: pure helpers that turn GET /partner/organization's flat rows into
// the tree the shared org chart engine draws, apply the Show filter and the host's own filters
// (status, currency, keyword), and open the path to every filter match. The Accounts and Partners
// chart views (ID-14095) reuse them with their pages' filters.

/** Which branches of the root are drawn. */
export const ORGANIZATION_SHOW_FILTERS = ['partnersAndDirectAccounts', 'partners', 'directAccounts'] as const;
export type OrganizationShowFilter = (typeof ORGANIZATION_SHOW_FILTERS)[number];

export const DEFAULT_ORGANIZATION_SHOW_FILTER: OrganizationShowFilter = 'partnersAndDirectAccounts';

export function isOrganizationShowFilter(value: string | null | undefined): value is OrganizationShowFilter {
  return ORGANIZATION_SHOW_FILTERS.some((filter) => filter === value);
}

/** The Overview's Status filter's vocabulary: a status its control offers, or '' for every status. */
export type OrganizationStatusFilter = PartnerAccountStatus | '';

export function isOrganizationStatusFilter(value: string | null | undefined): value is OrganizationStatusFilter {
  return value === '' || PARTNER_ACCOUNT_STATUSES.some((status) => status === value);
}

/**
 * What the tree is narrowed to: each part is one host's filter, '' for "no filter". The status is
 * whatever that host's own Status filter holds — the Overview's, the account listing's reported
 * status, a partner account's — and is matched against the entity's derived status.
 */
export type OrganizationFilter = {
  status: ReportedAccountStatus | SubPartnerAccountStatus | '';
  /** A country as the listings name it ("Canada"), which the entity's own must equal. */
  country: string;
  /** A currency the entity's own must equal. */
  currency: SupportedCurrencies | '';
  /**
   * Matched case-insensitively against the entity's name and id, and — for an account — the name of
   * the partner it sits under, as the listing's SearchText does. The listing also matches the legal
   * business name, which the organization rows do not carry.
   */
  keyword: string;
};

export const NO_ORGANIZATION_FILTER: OrganizationFilter = { status: '', country: '', currency: '', keyword: '' };

export function hasOrganizationFilter({ status, country, currency, keyword }: OrganizationFilter) {
  return status !== '' || country !== '' || currency !== '' || keyword.trim() !== '';
}

export function isSameOrganizationFilter(a: OrganizationFilter, b: OrganizationFilter) {
  return a.status === b.status && a.country === b.country && a.currency === b.currency && a.keyword === b.keyword;
}

function matchesOrganizationFilter(
  { entity, status, kind }: OrganizationNode,
  filter: OrganizationFilter,
  parent: OrganizationNode | undefined,
) {
  if (filter.status !== '' && status !== filter.status) return false;
  if (filter.country !== '' && entity.Country !== filter.country) return false;
  if (filter.currency !== '' && entity.Currency !== filter.currency) return false;

  const keyword = filter.keyword.trim().toLocaleLowerCase();
  const partnerName = kind === 'Account' && parent?.kind === 'Partner' ? parent.entity.Name : '';

  return (
    keyword === '' ||
    entity.Name.toLocaleLowerCase().includes(keyword) ||
    entity.EntityID.toLocaleLowerCase().includes(keyword) ||
    partnerName.toLocaleLowerCase().includes(keyword)
  );
}

/** The root is a partner row too, but its card reads "Organization". */
export type OrganizationEntityKind = 'Organization' | OrganizationEntityType;

// An interface, not a type alias: a type alias can't name itself as the engine node's child type.
export interface OrganizationNode extends OrgChartTreeNode<OrganizationNode, 'entity'> {
  entity: OrganizationEntity;
  kind: OrganizationEntityKind;
  /** Blocked, Active, or the reported onboarding stage — the rule the status badge reads by. Null when none. */
  status: DerivedPartnerAccountStatus;
  /** On the path to a Status filter match without matching itself: drawn, but faded. */
  isFaded: boolean;
  /** The ids from the root down to this node's parent. */
  ancestorIDs: string[];
}

export type OrganizationTreeOptions = {
  show: OrganizationShowFilter;
  filter: OrganizationFilter;
  displayLevel: number;
  pageSize: number;
  /**
   * A tree built from the same view earlier: every node it still holds keeps the expansion and page
   * it had there, so a refetch (an entity was created) does not close what the user had opened. New
   * nodes start from `displayLevel`.
   */
  expansionFrom?: OrganizationNode;
};

export const ORGANIZATION_ROOT_NODE_ID = 'organization';

/**
 * A partner and an account can carry the same `EntityID` (unique within each table, not across
 * them), so the node id carries the kind. Accounts and sub-accounts share one id space.
 */
export function organizationNodeID(entity: Pick<OrganizationEntity, 'EntityID' | 'EntityType'>, isRoot = false) {
  if (isRoot) return ORGANIZATION_ROOT_NODE_ID;

  return `${entity.EntityType === 'Partner' ? 'partner' : 'account'}:${entity.EntityID}`;
}

/** The node with this id, anywhere in the tree. */
export function findOrganizationNode(node: OrganizationNode, id: string): OrganizationNode | undefined {
  if (node.id === id) return node;

  for (const child of node.children ?? []) {
    const found = findOrganizationNode(child, id);
    if (found) return found;
  }

  return undefined;
}

/**
 * Resolved by type, never by `ParentEntityID` alone: a partner hangs off the root, an account off a
 * partner or the root, a sub-account off another account. A sub-account whose parent is not in the
 * book is drawn under the root rather than dropped — an org chart that silently loses a node is
 * worse than one that shows it unplaced.
 */
function resolveParentNodeID(
  entity: OrganizationEntity,
  rootEntityID: string,
  partnerIDs: ReadonlySet<string>,
  accountIDs: ReadonlySet<string>,
) {
  const parentID = entity.ParentEntityID;

  switch (entity.EntityType) {
    case 'Partner':
      return ORGANIZATION_ROOT_NODE_ID;
    case 'Account':
      return parentID && parentID !== rootEntityID && partnerIDs.has(parentID)
        ? `partner:${parentID}`
        : ORGANIZATION_ROOT_NODE_ID;
    case 'Sub-Account':
      return parentID && accountIDs.has(parentID) ? `account:${parentID}` : ORGANIZATION_ROOT_NODE_ID;
  }
}

function matchesShowFilter(entity: OrganizationEntity, show: OrganizationShowFilter) {
  switch (show) {
    case 'partners':
      return entity.EntityType === 'Partner';
    case 'directAccounts':
      return entity.EntityType !== 'Partner';
    case 'partnersAndDirectAccounts':
      return true;
  }
}

type Expansion = Pick<OrganizationNode, 'isExpanded' | 'childrenPage'>;

/** Every node's expansion and page, by id. */
function collectExpansion(node: OrganizationNode, into = new Map<string, Expansion>()) {
  into.set(node.id, { isExpanded: node.isExpanded, childrenPage: node.childrenPage });
  node.children?.forEach((child) => collectExpansion(child, into));

  return into;
}

/**
 * Keeps every match and every node on the path to one, drops the rest, and opens each kept branch so
 * the matches are on screen. Kept non-matches are faded; the root is always kept.
 */
function applyFilter(tree: OrganizationNode, filter: OrganizationFilter, pageSize: number): OrganizationNode {
  const matchIDs: string[] = [];

  const prune = (node: OrganizationNode, parent?: OrganizationNode): OrganizationNode | null => {
    const children = (node.children ?? [])
      .map((child) => prune(child, node))
      .filter((child): child is OrganizationNode => child !== null);

    const isMatch = matchesOrganizationFilter(node, filter, parent);

    if (isMatch) matchIDs.push(node.id);
    if (!isMatch && children.length === 0 && node.level !== 0) return null;

    return {
      ...node,
      children,
      numChildren: children.length,
      isMatch,
      isFaded: !isMatch,
      isExpanded: children.length > 0,
      childrenPage: children.length > 0 ? 1 : 0,
    };
  };

  // The root is never pruned, so the walk always yields a tree.
  const pruned = prune(tree) ?? { ...tree, children: [], numChildren: 0 };

  // Revealed last to first, so the page holding the first match wins: with more matches under one
  // parent than fit on a page, the rest sit behind "Show next" rather than the first behind "Show
  // previous".
  return matchIDs.reduceRight((filtered, matchID) => revealNode(filtered, matchID, pageSize), pruned);
}

/** The whole organization as the engine's tree, filtered and initially expanded to `displayLevel`. */
export function buildOrganizationTree(
  { Organization, Entities }: PartnerOrganization,
  options: OrganizationTreeOptions,
): OrganizationNode {
  const partnerIDs = new Set(
    Entities.filter((entity) => entity.EntityType === 'Partner').map((entity) => entity.EntityID),
  );

  const accountIDs = new Set(
    Entities.filter((entity) => entity.EntityType !== 'Partner').map((entity) => entity.EntityID),
  );

  const childrenByParent = new Map<string, OrganizationEntity[]>();

  for (const entity of Entities) {
    const parentNodeID = resolveParentNodeID(entity, Organization.EntityID, partnerIDs, accountIDs);
    const siblings = childrenByParent.get(parentNodeID) ?? [];

    siblings.push(entity);
    childrenByParent.set(parentNodeID, siblings);
  }

  const previousExpansion = options.expansionFrom && collectExpansion(options.expansionFrom);

  const expansionOf = (id: string, level: number, numChildren: number): Expansion => {
    const carried = previousExpansion?.get(id);

    if (!carried || numChildren === 0) return getInitialExpansion(level, numChildren > 0, options.displayLevel);

    // A child may have left the page the node was on.
    const lastPage = Math.ceil(numChildren / options.pageSize);

    return {
      isExpanded: carried.isExpanded,
      childrenPage: carried.isExpanded ? Math.min(Math.max(carried.childrenPage, 1), lastPage) : 0,
    };
  };

  const buildNode = (
    entity: OrganizationEntity,
    kind: OrganizationEntityKind,
    level: number,
    ancestorIDs: string[],
    childEntities: OrganizationEntity[],
  ): OrganizationNode => {
    const id = organizationNodeID(entity, kind === 'Organization');

    const children = childEntities.map((child) =>
      buildNode(
        child,
        child.EntityType,
        level + 1,
        [...ancestorIDs, id],
        childrenByParent.get(organizationNodeID(child)) ?? [],
      ),
    );

    return {
      ...expansionOf(id, level, children.length),
      id,
      parentId: ancestorIDs.at(-1) ?? '',
      linked: true,
      nodeType: 'entity',
      level,
      numChildren: children.length,
      children,
      entity,
      kind,
      status: derivePartnerAccountStatus(entity),
      isFaded: false,
      ancestorIDs,
    };
  };

  const rootChildren = (childrenByParent.get(ORGANIZATION_ROOT_NODE_ID) ?? []).filter((entity) =>
    matchesShowFilter(entity, options.show),
  );

  const tree = buildNode(Organization, 'Organization', 0, [], rootChildren);

  return hasOrganizationFilter(options.filter) ? applyFilter(tree, options.filter, options.pageSize) : tree;
}
